import { useModal } from 'vue-final-modal';
import { isNavigationFailure, useRouter } from 'vue-router';
import {
  GENERATED_RECORD_NOT_FOUND_MESSAGE,
  getGeneratedRecordConflictCode,
  getGeneratedRecordConflictMessage,
  getGenerationRefusalMessage,
  openGeneratedRecord,
  releaseGeneratedRecord,
} from '@/entities/GeneratedRecord';
import { isNotFoundError, useCustomToast } from '@/shared/lib';
import { ConfirmationModal } from '@/shared/ui';
import { useMatrixPageOrigin } from './useMatrixPageOrigin';

export const RECORD_IN_USE_MESSAGE = 'Registro em uso, tente novamente mais tarde.';
export const RECORD_IN_USE_HINT =
  'Um outro usuário está preenchendo o Registro, você pode utilizá-lo assim que ele for salvo novamente.';
export const RECORD_OPEN_ERROR_TITLE = 'Não foi possível abrir o Registro.';

/** `stale`: the row on screen disagrees with the server, so the list should fetch again. */
export type FillGeneratedRecordResult = 'opened' | 'stale' | 'failed';

function notFoundDetail(error: unknown): { message?: string } {
  return isNotFoundError(error) ? { message: GENERATED_RECORD_NOT_FOUND_MESSAGE } : {};
}

function isStaleRow(error: unknown): boolean {
  return isNotFoundError(error) || getGeneratedRecordConflictCode(error) !== null;
}

export function useFillGeneratedRecord() {
  const router = useRouter();
  const { prefix, returnQuery } = useMatrixPageOrigin();
  const { showErrorToast } = useCustomToast();

  const recordInUseModal = useModal({
    component: ConfirmationModal,
    attrs: {
      title: RECORD_IN_USE_MESSAGE,
      text: RECORD_IN_USE_HINT,
      actionButtonText: 'Ok',
      onConfirm: () => recordInUseModal.close(),
      onClose: () => recordInUseModal.close(),
    },
  });

  async function releaseQuietly(recordId: number, inUseToken: string): Promise<void> {
    try {
      await releaseGeneratedRecord(recordId, { inUseToken });
    } catch {
      // The person stays on the list either way; the server frees a lock nobody holds.
    }
  }

  // Opens before leaving the list, so a refusal keeps the person here with the reason. The fill
  // screen opens again on arrival (the same member may reopen and the token rotates), which keeps a
  // reload of that screen working without a token carried over in the history.
  async function fillGeneratedRecord(recordId: number): Promise<FillGeneratedRecordResult> {
    let inUseToken: string;
    try {
      ({ inUseToken } = await openGeneratedRecord(recordId));
    } catch (error) {
      const message = getGeneratedRecordConflictMessage(error) ?? getGenerationRefusalMessage(error);
      if (getGeneratedRecordConflictCode(error) === 'GENERATED_RECORD_IN_USE' && message !== null) {
        recordInUseModal.open();
      } else {
        showErrorToast({
          title: RECORD_OPEN_ERROR_TITLE,
          type: 'error',
          ...(message !== null ? { message } : notFoundDetail(error)),
        });
      }
      return isStaleRow(error) ? 'stale' : 'failed';
    }

    try {
      const failure = await router.push({
        name: 'PreencherRegistro',
        params: { prefix: prefix(), recordId: String(recordId) },
        ...returnQuery(),
      });
      if (isNavigationFailure(failure)) await releaseQuietly(recordId, inUseToken);
    } catch {
      await releaseQuietly(recordId, inUseToken);
    }
    return 'opened';
  }

  return { fillGeneratedRecord };
}
